import React, {useEffect, useMemo, useRef, useState} from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {useAuth} from "../contexts/AuthContext";
import {adminMenuConfig, getVisibleMenuItems} from "../config/adminMenuConfig";
import {useMenuBadges} from "../utils/inquirySummary";

const Badge = ({count}) => (count > 0 ? (
    <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
        {count > 99 ? '99+' : count}
    </span>
) : null);

const startsWithSegment = (path, base) => {
    const b = base.endsWith('/') ? base : base + '/';
    return path === base || path.startsWith(b);
};

const isItemActive = (pathname, item) => {
    if (item.children?.length) {
        return startsWithSegment(pathname, item.path);
    }

    return pathname === item.path;
};

const LeftMenu = ({isMobileOpen, onCloseMobile}) => {
    const {t} = useTranslation();
    const {user} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const previousPathRef = useRef(location.pathname);
    const [expandedItems, setExpandedItems] = useState({});
    const badges = useMenuBadges(user?.profileList || []);
    const visibleMenuItems = useMemo(() => {
        return getVisibleMenuItems(adminMenuConfig, user?.profileList || []);
    }, [user?.profileList]);

    useEffect(() => {
        const activeParents = visibleMenuItems
            .filter(item => item.children?.length && startsWithSegment(location.pathname, item.path))
            .map(item => item.id);

        if (activeParents.length) {
            setExpandedItems(prev => activeParents.reduce((acc, id) => ({...acc, [id]: prev[id] ?? true}), {}));
        }
    }, [location.pathname, visibleMenuItems]);

    useEffect(() => {
        const pathChanged = previousPathRef.current !== location.pathname;
        if (pathChanged && isMobileOpen) {
            onCloseMobile?.();
        }
        previousPathRef.current = location.pathname;
    }, [location.pathname, isMobileOpen, onCloseMobile]);

    const toggleSubMenu = (itemId) => {
        setExpandedItems(prev => ({...prev, [itemId]: !prev[itemId]}));
    };

    const getItemClass = (active) =>
        active ? "block p-2 hover:bg-gray-700 rounded bg-gray-900" : "block p-2 hover:bg-gray-700 rounded";

    const navigateToMenuItem = (item) => {
        const targetPath = item.defaultChildPath || item.path;
        navigate(targetPath);
    };

    return (
        <>
            <div
                className={`fixed inset-0 z-30 bg-black/50 transition-opacity md:hidden ${
                    isMobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
                onClick={onCloseMobile}
            />

            <nav
                className={`fixed inset-y-0 left-0 z-40 w-64 transform overflow-y-auto bg-gray-800 p-2 text-white transition-transform md:relative md:z-0 md:translate-x-0 ${
                    isMobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <div className="mb-3 flex items-center justify-between px-3 pt-2 md:hidden">
                    <span className="font-semibold">Menu</span>
                    <button className="rounded p-1 hover:bg-gray-700" onClick={onCloseMobile} aria-label="Zavřít menu">
                        ✕
                    </button>
                </div>

                <ul>
                    {visibleMenuItems.map((item) => (
                        <li key={item.id} className="p-3">
                            <div className="flex items-center gap-2">
                                {item.children?.length ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                toggleSubMenu(item.id);
                                                navigateToMenuItem(item);
                                            }}
                                            className={`flex flex-1 items-center justify-between text-left ${getItemClass(isItemActive(location.pathname, item))}`}
                                        >
                                            {t(item.labelKey)}
                                            {item.badge && !expandedItems[item.id] ? <Badge count={badges[item.badge]}/> : null}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleSubMenu(item.id)}
                                            className="rounded p-2 hover:bg-gray-700"
                                            aria-label="Rozbalit submenu"
                                        >
                                            {expandedItems[item.id] ? "▾" : "▸"}
                                        </button>
                                    </>
                                ) : (
                                    <NavLink to={item.path} className={({isActive}) => getItemClass(isActive)}>
                                        {t(item.labelKey)}
                                        {item.badge ? <Badge count={badges[item.badge]}/> : null}
                                    </NavLink>
                                )}
                            </div>

                            {item.children?.length && expandedItems[item.id] && (
                                <ul className="pl-4">
                                    {item.children.map((subItem) => (
                                        <li key={subItem.id}>
                                            <NavLink to={subItem.path} end className={({isActive}) => getItemClass(isActive)}>
                                                {t(subItem.labelKey)}
                                                {subItem.badge ? <Badge count={badges[subItem.badge]}/> : null}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            </nav>
        </>
    );
};

export default LeftMenu;
